import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getAllRecords, STORES, putRecords } from '../lib/db/indexed-db'
import { setHasCachedData } from '@store/slices/offlineSlice'
import { Cigar } from '@store/api/cigarApi'
import { SmokingSession } from '@store/api/sessionApi'

export interface UseOfflineCollectionResult {
  offlineCigars: Cigar[]
  offlineSessions: SmokingSession[]
  isLoadingOffline: boolean
  cacheCollection: (cigars: Cigar[]) => Promise<void>
  cacheSessions: (sessions: SmokingSession[]) => Promise<void>
  reloadOfflineData: () => Promise<void>
}

export function useOfflineCollection(): UseOfflineCollectionResult {
  const { isOnline, pendingCount } = useAppSelector((state) => state.offline)
  const dispatch = useAppDispatch()
  const [offlineCigars, setOfflineCigars] = useState<Cigar[]>([])
  const [offlineSessions, setOfflineSessions] = useState<SmokingSession[]>([])
  const [isLoadingOffline, setIsLoadingOffline] = useState(false)

  const reloadOfflineData = useCallback(async () => {
    setIsLoadingOffline(true)
    try {
      const [cigars, sessions] = await Promise.all([
        getAllRecords<Cigar>(STORES.CIGARS),
        getAllRecords<SmokingSession>(STORES.SESSIONS),
      ])
      setOfflineCigars(cigars)
      setOfflineSessions(sessions)
      dispatch(setHasCachedData(cigars.length > 0 || sessions.length > 0))
    } catch (err) {
      console.error('Failed to load offline data:', err)
    } finally {
      setIsLoadingOffline(false)
    }
  }, [dispatch])

  useEffect(() => {
    if (!isOnline) {
      reloadOfflineData()
    }
  }, [isOnline, pendingCount, reloadOfflineData])

  const cacheCollection = async (cigars: Cigar[]): Promise<void> => {
    try {
      await putRecords(STORES.CIGARS, cigars)
      dispatch(setHasCachedData(cigars.length > 0))
      if (!isOnline) {
        setOfflineCigars(cigars)
      }
    } catch (err) {
      console.error('Failed to cache collection:', err)
    }
  }

  const cacheSessions = async (sessions: SmokingSession[]): Promise<void> => {
    try {
      await putRecords(STORES.SESSIONS, sessions)
      dispatch(setHasCachedData(sessions.length > 0))
      if (!isOnline) {
        setOfflineSessions(sessions)
      }
    } catch (err) {
      console.error('Failed to cache sessions:', err)
    }
  }

  return {
    offlineCigars,
    offlineSessions,
    isLoadingOffline,
    cacheCollection,
    cacheSessions,
    reloadOfflineData,
  }
}
